// Multi-territory Compliance Master form.
// Ported from the India ComplianceMasterForm (src/components/admin/ComplianceMasterForm.jsx).

import calendarIcon from '@/assets/calendar.svg'
import CustomInput from '@/components/Returns/CustomInput'
import CustomMultiSelect from '@/components/Returns/CustomMultiSelect'
import { MTCustomSelect } from '@/multi-territory/mt-components/mt-shared/MTCustomSelect'
import {
    getFinancialYearForDate,
    MIN_COMPLIANCE_PERIOD_DATE,
} from '@/utils/complianceMasterUtils'
import {
    Box,
    Button,
    Grid,
    type SxProps,
    type Theme,
    Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

const MTInput = CustomInput as any
const MTMultiSelect = CustomMultiSelect as any
const MTSingleSelect = MTCustomSelect as any

export interface MTComplianceCountryOption {
  countryCode: string
  countryName: string
}

export interface MTComplianceFormValues {
  country: string
  entityNames: string[]
  entityTANs: string[]
  complianceCategories: string[]
  compliancePeriod: string
  compliancePeriodDate: string
  dueDate: string
  status: string
  completionDate: string
}

export interface MTComplianceCategoryOption {
  name: string
  frequency?: string
}

export interface MTComplianceTanItem {
  tan: string
  entityName: string
  [key: string]: unknown
}

export interface MTComplianceEntityOption {
  id?: string
  uuid?: string
  name: string
  countryCode?: string
  countryName?: string
}

export interface MTAdminComplianceMasterFormProps {
  mode?: 'add' | 'edit'
  values: MTComplianceFormValues
  countryOptions?: MTComplianceCountryOption[]
  entities?: MTComplianceEntityOption[]
  tanList?: MTComplianceTanItem[]
  categories?: MTComplianceCategoryOption[]
  formErrors?: Record<string, string>
  formLoading?: boolean
  onFieldChange: (field: keyof MTComplianceFormValues, value: unknown) => void
  onSubmit: (e: React.FormEvent | React.MouseEvent) => void
}

const CalendarIcon = (props: Record<string, unknown>) => (
  <img src={calendarIcon} alt="" width={20} height={20} {...props} />
)

const FieldLabel = ({
  children,
  required = false,
}: {
  children: React.ReactNode
  required?: boolean
}) => (
  <Typography
    sx={{
      fontSize: '16px',
      fontWeight: 500,
      color: 'var(--forbrand-primary)',
      mb: 1,
      lineHeight: '140%',
    }}
  >
    {children}
    {required && <span style={{ color: 'var(--forbrand-error)' }}>*</span>}
  </Typography>
)

const datePickerSlotProps = ({
  error,
  helperText,
  disabled,
}: { error?: boolean; helperText?: string; disabled?: boolean } = {}) => ({
  textField: {
    fullWidth: true,
    error,
    helperText,
    sx: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: disabled ? 'var(--bg-disabled)' : '#fff',
        fontSize: '14px',
        height: '48px',
        borderRadius: 'unset',
        '& fieldset': {
          borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
        },
        '&:hover fieldset': {
          borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
        },
        '&.Mui-focused fieldset': {
          borderColor: error
            ? 'var(--forbrand-error)'
            : 'var(--forbrand-color)',
        },
        '&.Mui-disabled': {
          backgroundColor: 'var(--bg-disabled)',
        },
      },
      '& .MuiInputBase-input': {
        fontSize: '14px',
        color: '#374151',
      },
    },
  },
})

export function MTAdminComplianceMasterForm({
  mode = 'add',
  values,
  countryOptions = [],
  entities = [],
  tanList = [],
  categories = [],
  formErrors = {},
  formLoading = false,
  onFieldChange,
  onSubmit,
}: MTAdminComplianceMasterFormProps) {
  const isEdit = mode === 'edit'

  // Get available TANs based on the currently selected Entity Names
  const getAvailableTANs = (selectedEntityNames: string[]): string[] => {
    if (!selectedEntityNames || selectedEntityNames.length === 0) return []
    if (!tanList || tanList.length === 0) return []
    const availableTANs = tanList
      .filter(tanItem => selectedEntityNames.includes(tanItem.entityName))
      .map(tanItem => tanItem.tan)
    return [...new Set(availableTANs)]
  }

  const availableTANs = getAvailableTANs(values.entityNames)

  const compliancePeriodDate = values.compliancePeriodDate
    ? dayjs(values.compliancePeriodDate, 'DD/MM/YYYY')
    : null
  const completionDateValue = values.completionDate
    ? dayjs(values.completionDate, 'DD/MM/YYYY')
    : null

  const handleCompliancePeriodChange = (newDate: dayjs.Dayjs | null) => {
    if (!newDate) return
    const jsDate = newDate.toDate()
    const derivedFY = getFinancialYearForDate(jsDate)
    onFieldChange('compliancePeriodDate', newDate.format('DD/MM/YYYY'))
    onFieldChange('compliancePeriod', derivedFY)
  }

  const handleCompletionDateChange = (newDate: dayjs.Dayjs | null) => {
    onFieldChange('completionDate', newDate ? newDate.format('DD/MM/YYYY') : '')
  }

  // In edit mode, disabled fields should not look greyed out.
  const editModeSx: SxProps<Theme> = isEdit
    ? {
        '--bg-disabled': '#ffffff',
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: '#374151',
        },
        '& .MuiSelect-select.Mui-disabled': {
          WebkitTextFillColor: '#374151',
        },
      }
    : {}

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={editModeSx}>
        {/* Mandatory fields note (add mode only) */}
        {!isEdit && (
          <Typography
            variant="body2"
            sx={{
              color: 'var(--forbrand-error)',
              mb: 3,
              fontSize: '14px',
            }}
          >
            (*) Fields are mandatory
          </Typography>
        )}

        {/* Form container */}
        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            {/* Country */}
            <Grid size={6}>
              <MTSingleSelect
                label="Country"
                fullWidth
                required
                placeholder="Select Country"
                value={values.country || ''}
                onChange={(e: { target: { value: string } }) =>
                  onFieldChange('country', e.target.value)
                }
                options={countryOptions}
                getOptionLabel={(country: MTComplianceCountryOption) =>
                  country.countryName
                }
                getOptionValue={(country: MTComplianceCountryOption) =>
                  country.countryCode
                }
                disabled={isEdit}
                error={!!formErrors.country}
                helperText={formErrors.country}
              />
            </Grid>

            {/* Compliance Category */}
            <Grid size={6}>
              <MTMultiSelect
                label="Compliance Category"
                fullWidth
                required
                placeholder="Select Compliance Category"
                value={values.complianceCategories || []}
                onChange={(e: { target: { value: string[] } }) =>
                  onFieldChange('complianceCategories', e.target.value)
                }
                options={categories}
                getOptionLabel={(category: MTComplianceCategoryOption) =>
                  category.name
                }
                getOptionValue={(category: MTComplianceCategoryOption) =>
                  category.name
                }
                disabled={isEdit || !values.country}
                error={!!formErrors.complianceCategories}
                helperText={formErrors.complianceCategories}
              />
            </Grid>

            {/* Entity Name */}
            <Grid size={6}>
              <MTMultiSelect
                label="Entity Name"
                fullWidth
                required
                placeholder="Select Entity Name"
                value={values.entityNames || []}
                onChange={(e: { target: { value: string[] } }) =>
                  onFieldChange('entityNames', e.target.value)
                }
                options={entities}
                getOptionLabel={(entity: MTComplianceEntityOption) =>
                  entity.name
                }
                getOptionValue={(entity: MTComplianceEntityOption) =>
                  entity.name
                }
                disabled={isEdit || !values.country}
                error={!!formErrors.entityNames}
                helperText={formErrors.entityNames}
              />
            </Grid>

            {/* Entity TAN */}
            <Grid size={6}>
              <MTMultiSelect
                label="Entity TIN/UIN"
                fullWidth
                required
                placeholder="Select Entity TIN/UIN"
                value={values.entityTANs || []}
                onChange={(e: { target: { value: string[] } }) =>
                  onFieldChange('entityTANs', e.target.value)
                }
                options={availableTANs}
                disabled={
                  isEdit ||
                  !values.entityNames ||
                  values.entityNames.length === 0
                }
                error={!!formErrors.entityTANs}
                helperText={formErrors.entityTANs}
              />
            </Grid>

            {/* Start Date (editable in add mode, disabled in edit mode) */}
            <Grid size={6}>
              <FieldLabel required>Start Date</FieldLabel>
              <DatePicker
                value={compliancePeriodDate}
                onChange={handleCompliancePeriodChange}
                format="DD/MM/YYYY"
                minDate={dayjs(MIN_COMPLIANCE_PERIOD_DATE)}
                maxDate={dayjs()}
                disabled={isEdit || !values.country}
                slots={{ openPickerIcon: CalendarIcon }}
                slotProps={datePickerSlotProps({
                  error: !!formErrors.compliancePeriod,
                  helperText: formErrors.compliancePeriod,
                  disabled: isEdit || !values.country,
                })}
              />
            </Grid>

            {/* Edit-only fields: Completion Date (editable) then Status (disabled) */}
            {isEdit && (
              <>
                <Grid size={6}>
                  <FieldLabel>Completion Date</FieldLabel>
                  <DatePicker
                    value={completionDateValue}
                    onChange={handleCompletionDateChange}
                    format="DD/MM/YYYY"
                    slots={{ openPickerIcon: CalendarIcon }}
                    slotProps={datePickerSlotProps({
                      error: !!formErrors.completionDate,
                      helperText: formErrors.completionDate,
                    })}
                  />
                </Grid>

                <Grid size={6}>
                  <MTInput
                    label="Status"
                    fullWidth
                    placeholder="Status"
                    value={values.status || ''}
                    disabled
                  />
                </Grid>
              </>
            )}
          </Grid>

          {formErrors.general && (
            <Typography variant="body2" color="error" sx={{ my: 2 }}>
              {formErrors.general}
            </Typography>
          )}
        </Box>

        {/* Submit button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mt: 3,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formLoading || (!isEdit && !values.country)}
            onClick={onSubmit}
            sx={{
              backgroundColor: 'var( --forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              textTransform: 'none',
              px: '16px',
              py: '8px',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: '4px',
              '&:disabled': {
                backgroundColor: '#ccc',
                boxShadow: 'none',
              },
            }}
          >
            {formLoading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  )
}

export default MTAdminComplianceMasterForm
